
import { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';

interface CalculatorResults {
  monthlyPremium: number;
  annualPremium: number;
  cashValueYear10: number;
  cashValueYear20: number;
  cashValueYear30: number;
  deathBenefit: number;
}

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  smsConsent: boolean;
}

const WholeLifeCalculator = () => {
  const [age, setAge] = useState(35);
  const [coverage, setCoverage] = useState(500000);
  const [healthClass, setHealthClass] = useState<'excellent' | 'good' | 'standard'>('excellent');
  const [paymentFrequency, setPaymentFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [leadFormData, setLeadFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    smsConsent: false,
  });
  const [results, setResults] = useState<CalculatorResults>({
    monthlyPremium: 0,
    annualPremium: 0,
    cashValueYear10: 0,
    cashValueYear20: 0,
    cashValueYear30: 0,
    deathBenefit: 0,
  });

  // Calculate whole life premiums and cash value projections
  const calculateWholeLife = (
    selectedAge: number,
    selectedCoverage: number,
    selectedHealth: string,
    frequency: string,
  ) => {
    // Base rate per $1,000 of coverage for whole life
    let baseRate = 0;

    // Age-based rate tiers (whole life is significantly more expensive than term)
    if (selectedAge >= 18 && selectedAge <= 30) {
      baseRate = 0.65;
    } else if (selectedAge >= 31 && selectedAge <= 40) {
      baseRate = 0.85;
    } else if (selectedAge >= 41 && selectedAge <= 50) {
      baseRate = 1.35;
    } else if (selectedAge >= 51 && selectedAge <= 60) {
      baseRate = 2.15;
    } else if (selectedAge >= 61 && selectedAge <= 75) {
      baseRate = 3.45;
    }

    // Health class multiplier
    const healthMultiplier =
      selectedHealth === 'excellent'
        ? 1.0
        : selectedHealth === 'good'
        ? 1.15
        : 1.35; // standard

    // Calculate monthly premium
    const monthlyPremium = (selectedCoverage / 1000) * baseRate * healthMultiplier;
    const annualPremium = monthlyPremium * 12 * 0.95; // 5% discount for annual payment

    // Cash value projections (whole life builds cash value over time)
    // Year 10: approximately 70% of premiums paid
    const totalPaid10 = monthlyPremium * 12 * 10;
    const cashValue10 = totalPaid10 * 0.7;

    // Year 20: approximately 96% of premiums paid
    const totalPaid20 = monthlyPremium * 12 * 20;
    const cashValue20 = totalPaid20 * 0.96;

    // Year 30: approximately 125% of premiums paid (compound growth kicks in)
    const totalPaid30 = monthlyPremium * 12 * 30;
    const cashValue30 = totalPaid30 * 1.25;

    setResults({
      monthlyPremium: Math.round(monthlyPremium * 100) / 100,
      annualPremium: Math.round(annualPremium * 100) / 100,
      cashValueYear10: Math.round(cashValue10),
      cashValueYear20: Math.round(cashValue20),
      cashValueYear30: Math.round(cashValue30),
      deathBenefit: selectedCoverage,
    });
  };

  useEffect(() => {
    calculateWholeLife(age, coverage, healthClass, paymentFrequency);
  }, [age, coverage, healthClass, paymentFrequency]);

  const coverageOptions = [
    { value: 25000, label: '$25,000', category: 'Final Expense' },
    { value: 50000, label: '$50,000', category: 'Final Expense' },
    { value: 100000, label: '$100,000', category: 'Whole Life' },
    { value: 250000, label: '$250,000', category: 'Whole Life' },
    { value: 500000, label: '$500,000', category: 'Whole Life' },
    { value: 750000, label: '$750,000', category: 'Whole Life' },
    { value: 1000000, label: '$1,000,000', category: 'Whole Life' },
    { value: 1500000, label: '$1,500,000', category: 'Whole Life' },
    { value: 2000000, label: '$2,000,000', category: 'Whole Life' },
  ];

  const healthOptions = [
    { value: 'excellent', label: 'Excellent', description: 'No health issues, non-smoker' },
    { value: 'good', label: 'Good', description: 'Minor health issues, well-managed' },
    { value: 'standard', label: 'Standard', description: 'Some health concerns or smoker' },
  ];

  // Generate chart data for cash value growth visualization
  const generateChartData = () => {
    const years = [5, 10, 15, 20, 25, 30];
    return years.map((year) => {
      const totalPaid = results.monthlyPremium * 12 * year;
      let cashValue = 0;

      if (year === 5) cashValue = totalPaid * 0.5;
      else if (year === 10) cashValue = results.cashValueYear10;
      else if (year === 15) cashValue = totalPaid * 0.85;
      else if (year === 20) cashValue = results.cashValueYear20;
      else if (year === 25) cashValue = totalPaid * 1.1;
      else if (year === 30) cashValue = results.cashValueYear30;

      return {
        year,
        totalPaid: Math.round(totalPaid),
        cashValue: Math.round(cashValue),
      };
    });
  };

  const chartData = generateChartData();
  const maxValue = Math.max(...chartData.map((d) => Math.max(d.totalPaid, d.cashValue)));

  // Handle lead form submission
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Insert lead into Supabase
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert({
          name: leadFormData.name,
          email: leadFormData.email,
          phone: leadFormData.phone,
          source: 'Whole Life Calculator',
          status: 'new',
          lead_type: 'calculator',
          notes: `Calculator inputs: Age ${age}, Coverage $${coverage.toLocaleString()}, Health Class ${healthClass}, Estimated Premium $${paymentFrequency === 'monthly' ? results.monthlyPremium : results.annualPremium}/${paymentFrequency === 'monthly' ? 'mo' : 'yr'}`,
          sms_consent: leadFormData.smsConsent,
        })
        .select()
        .maybeSingle();

      if (leadError) throw leadError;

      // Store calculator results
      if (leadData) {
        await supabase.from('calculator_results').insert({
          lead_id: leadData.id,
          calculator_type: 'whole_life_premium',
          inputs: {
            age,
            coverage,
            healthClass,
            paymentFrequency,
          },
          results: {
            monthlyPremium: results.monthlyPremium,
            annualPremium: results.annualPremium,
            cashValueYear10: results.cashValueYear10,
            cashValueYear20: results.cashValueYear20,
            cashValueYear30: results.cashValueYear30,
            deathBenefit: results.deathBenefit,
          },
        });
      }

      // Trigger email notification via edge function
      await supabase.functions.invoke('send-email', {
        body: {
          to: leadFormData.email,
          subject: 'Your Whole Life Insurance Quote Estimate',
          html: `
            <h2>Thank you for using our Whole Life Calculator!</h2>
            <p>Hi ${leadFormData.name},</p>
            <p>Based on your inputs, here's your personalized whole life insurance estimate:</p>
            <ul>
              <li><strong>Age:</strong> ${age}</li>
              <li><strong>Coverage Amount:</strong> $${coverage.toLocaleString()}</li>
              <li><strong>Health Class:</strong> ${healthClass.charAt(0).toUpperCase() + healthClass.slice(1)}</li>
              <li><strong>Estimated ${paymentFrequency === 'monthly' ? 'Monthly' : 'Annual'} Premium:</strong> $${paymentFrequency === 'monthly' ? results.monthlyPremium.toFixed(2) : results.annualPremium.toFixed(2)}</li>
            </ul>
            <h3>Projected Cash Value Growth:</h3>
            <ul>
              <li><strong>Year 10:</strong> $${results.cashValueYear10.toLocaleString()}</li>
              <li><strong>Year 20:</strong> $${results.cashValueYear20.toLocaleString()}</li>
              <li><strong>Year 30:</strong> $${results.cashValueYear30.toLocaleString()}</li>
            </ul>
            <p>Your whole life policy builds guaranteed cash value you can borrow against tax-free!</p>
            <p>One of our licensed agents will contact you within 24 hours to provide your exact quote and answer any questions.</p>
            <p>Best regards,<br/>Hoosier Life Insurance Team</p>
          `,
        },
      });

      setSubmitSuccess(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('There was an error submitting your information. Please try again or call us directly at (317) 648-2983.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayPremium = paymentFrequency === 'monthly' ? results.monthlyPremium : results.annualPremium;
  const premiumLabel = paymentFrequency === 'monthly' ? '/mo' : '/yr';

  return (
    <section className="py-20 bg-[#0a1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Whole Life Insurance Calculator</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See your estimated premium and projected cash value growth. Adjust the inputs below to get your personalized whole life insurance estimate.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-12">
          {/* Input Controls */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Age Slider */}
            <div>
              <label className="block text-white font-semibold mb-3">Your Age</label>
              <div className="space-y-4">
                <input
                  type="range"
                  min="18"
                  max="75"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#D97642]"
                  style={{
                    background: `linear-gradient(to right, #D97642 0%, #D97642 ${((age - 18) / 57) * 100}%, rgba(255,255,255,0.2) ${((age - 18) / 57) * 100}%, rgba(255,255,255,0.2) 100%)`,
                  }}
                />
                <div className="flex justify-between text-sm text-gray-400">
                  <span>18</span>
                  <span className="text-2xl font-bold text-[#D97642]">{age}</span>
                  <span>75</span>
                </div>
              </div>
            </div>

            {/* Coverage Amount Dropdown */}
            <div>
              <label className="block text-white font-semibold mb-3">Coverage Amount</label>
              <select
                value={coverage}
                onChange={(e) => setCoverage(Number(e.target.value))}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white text-lg font-semibold cursor-pointer hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97642]"
              >
                {coverageOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#0a1628] text-white">
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-400 mt-2">{coverage < 100000 ? 'Final Expense Coverage' : 'Whole Life Coverage'}</div>
            </div>

            {/* Health Class Selector */}
            <div>
              <label className="block text-white font-semibold mb-3">Health Class</label>
              <div className="space-y-2">
                {healthOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setHealthClass(option.value as 'excellent' | 'good' | 'standard')}
                    className={`w-full px-4 py-2 rounded-lg font-medium transition-all text-left ${
                      healthClass === option.value
                        ? 'bg-[#D97642] text-white shadow-lg shadow-[#D97642]/30'
                        : 'bg-white/10 text-gray-300 hover:bg-white/15 border border-white/20'
                    }`}
                  >
                    <div className="font-semibold text-sm">{option.label}</div>
                    <div className="text-xs opacity-80">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Frequency */}
            <div>
              <label className="block text-white font-semibold mb-3">Payment Frequency</label>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setPaymentFrequency('monthly')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    paymentFrequency === 'monthly'
                      ? 'bg-[#D97642] text-white shadow-lg shadow-[#D97642]/30'
                      : 'bg-white/10 text-gray-300 hover:bg-white/15 border border-white/20'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setPaymentFrequency('annual')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    paymentFrequency === 'annual'
                      ? 'bg-[#D97642] text-white shadow-lg shadow-[#D97642]/30'
                      : 'bg-white/10 text-gray-300 hover:bg-white/15 border border-white/20'
                  }`}
                >
                  Annual
                  <span className="text-xs block opacity-80">Save 5%</span>
                </button>
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Estimated Premium */}
            <div className="bg-gradient-to-br from-[#D97642]/20 to-[#D97642]/5 border border-[#D97642]/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-money-dollar-circle-line text-[#D97642] text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Estimated Premium</h3>
              </div>
              <p className="text-3xl font-bold text-white">
                ${displayPremium.toFixed(2)}
                <span className="text-base font-normal text-gray-400">{premiumLabel}</span>
              </p>
            </div>

            {/* Cash Value Year 10 */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-line-chart-line text-gray-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Cash Value (Year 10)</h3>
              </div>
              <p className="text-3xl font-bold text-white">
                ${results.cashValueYear10.toLocaleString()}
              </p>
            </div>

            {/* Cash Value Year 20 */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-line-chart-line text-gray-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Cash Value (Year 20)</h3>
              </div>
              <p className="text-3xl font-bold text-white">
                ${results.cashValueYear20.toLocaleString()}
              </p>
            </div>

            {/* Cash Value Year 30 */}
            <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-trophy-line text-green-400 text-xl"></i>
                <h3 className="text-sm font-medium text-gray-300">Cash Value (Year 30)</h3>
              </div>
              <p className="text-3xl font-bold text-green-400">
                ${results.cashValueYear30.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Visual Cash Value Growth Chart */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-[#D97642]"></i>
              Cash Value Growth Over Time
            </h3>
            <div className="space-y-6">
              {chartData.map((data) => (
                <div key={data.year}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-300">Year {data.year}</span>
                    <span className="text-sm text-gray-400">
                      Cash Value: ${data.cashValue.toLocaleString()} | Paid: ${data.totalPaid.toLocaleString()}
                    </span>
                  </div>
                  <div className="relative h-12 bg-white/5 rounded-lg overflow-hidden">
                    {/* Total Paid Bar (outline) */}
                    <div
                      className="absolute left-0 top-0 h-full border-2 border-dashed border-gray-400 flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${(data.totalPaid / maxValue) * 100}%` }}
                    >
                      <span className="text-xs font-bold text-gray-300 whitespace-nowrap">
                        ${data.totalPaid.toLocaleString()}
                      </span>
                    </div>
                    {/* Cash Value Bar */}
                    <div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#D97642] to-[#C26638] flex items-center justify-end pr-3 transition-all duration-500"
                      style={{ width: `${(data.cashValue / maxValue) * 100}%` }}
                    >
                      <span className="text-xs font-bold text-white whitespace-nowrap">
                        ${data.cashValue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-r from-[#D97642] to-[#C26638] rounded"></div>
                <span className="text-sm text-gray-300">Cash Value</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-dashed border-gray-400 rounded"></div>
                <span className="text-sm text-gray-300">Total Premiums Paid</span>
              </div>
            </div>
          </div>

          {/* Key Benefits Callout */}
          <div className="bg-gradient-to-br from-[#D97642]/10 to-transparent border border-[#D97642]/20 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-[#D97642]"></i>
              Why Whole Life Insurance?
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-infinity-line text-[#D97642]"></i>
                  <h4 className="font-semibold text-white text-sm">Lifetime Coverage</h4>
                </div>
                <p className="text-xs text-gray-300">Never expires. Coverage lasts until age 100 with fixed premiums.</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-hand-coin-line text-[#D97642]"></i>
                  <h4 className="font-semibold text-white text-sm">Borrow Tax-Free</h4>
                </div>
                <p className="text-xs text-gray-300">Access your cash value through low-interest policy loans anytime.</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-shield-line text-[#D97642]"></i>
                  <h4 className="font-semibold text-white text-sm">Guaranteed Growth</h4>
                </div>
                <p className="text-xs text-gray-300">Cash value grows at a guaranteed rate with zero market risk.</p>
              </div>
            </div>
          </div>

          {/* Lead Capture Form or CTA */}
          {!showLeadForm && !submitSuccess && (
            <div className="text-center space-y-4">
              <button
                onClick={() => setShowLeadForm(true)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors shadow-lg shadow-[#D97642]/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line text-xl"></i>
                Get My Exact Quote
              </button>
              <p className="text-sm text-gray-400 max-w-2xl mx-auto">
                <i className="ri-information-line mr-1"></i>
                These are estimated rates for illustrative purposes only. Actual premiums vary based on health status,
                lifestyle factors, carrier, and underwriting.
              </p>
            </div>
          )}

          {/* Lead Capture Form */}
          {showLeadForm && !submitSuccess && (
            <div className="bg-gradient-to-br from-[#D97642]/10 to-transparent border border-[#D97642]/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-2 text-center">Get Your Personalized Quote</h3>
              <p className="text-gray-300 mb-6 text-center">
                A licensed agent will contact you within 24 hours with your exact quote
              </p>
              <form onSubmit={handleLeadSubmit} className="space-y-4 max-w-2xl mx-auto">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={leadFormData.name}
                      onChange={(e) => setLeadFormData({ ...leadFormData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={leadFormData.email}
                      onChange={(e) => setLeadFormData({ ...leadFormData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2 text-sm">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={leadFormData.phone}
                    onChange={(e) => setLeadFormData({ ...leadFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D97642]"
                    placeholder="(317) 555-0123"
                  />
                </div>
                <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    checked={leadFormData.smsConsent}
                    onChange={(e) => setLeadFormData({ ...leadFormData, smsConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#D97642] cursor-pointer"
                  />
                  <label htmlFor="smsConsent" className="text-sm text-gray-300 cursor-pointer">
                    I consent to receive text messages about my quote and policy information. Message and data rates may
                    apply.
                  </label>
                </div>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowLeadForm(false)}
                    className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-fill"></i>
                        Submit Request
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Success Message */}
          {submitSuccess && (
            <div className="bg-gradient-to-br from-green-500/20 to-transparent border border-green-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-4xl text-green-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Quote Request Received!</h3>
              <p className="text-gray-300 mb-6">
                Thank you for your interest! We've sent your estimate to <strong>{leadFormData.email}</strong>.
                A licensed agent will contact you within 24 hours to provide your exact quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/book-appointment"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D97642] hover:bg-[#C26638] text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-calendar-line"></i>
                  Schedule a Call Now
                </a>
                <button
                  onClick={() => {
                    setSubmitSuccess(false);
                    setLeadFormData({ name: '', email: '', phone: '', smsConsent: false });
                  }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calculator-line"></i>
                  Calculate Another Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WholeLifeCalculator;
